import React, { useEffect, useState } from 'react'
import { ArrowLeft, Save, RotateCcw } from 'lucide-react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../components/Common/Breadcrumb'
import subscriptionPaymentService from '../../services/subscriptionPaymentService'
import subscriptionService from '../../services/subscriptionService'
import { extractSubscriptions, PAYMENT_METHODS, PAYMENT_STATUSES } from './subscriptionUtils'

const emptyForm = () => ({
  paymentNumber: '',
  subscription: '',
  amount: 0,
  currency: 'INR',
  exchangeRate: 1,
  inrAmount: 0,
  taxAmount: 0,
  rcmAmount: 0,
  totalPaid: 0,
  paymentDate: new Date().toISOString().split('T')[0],
  dueDate: '',
  invoiceDate: '',
  vendorInvoiceNumber: '',
  billingPeriodStart: '',
  billingPeriodEnd: '',
  status: 'Expected',
  paymentMethod: 'Invoice',
  reference: '',
  invoiceAttachmentUrl: '',
  paymentProofUrl: '',
  linkedExpense: '',
  linkedVendorInvoice: '',
  linkedAccountingEntry: '',
  notes: '',
})

const PaymentForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const isView = location.pathname.includes('/view/')
  const isEdit = location.pathname.includes('/edit/')
  const isCreate = location.pathname.endsWith('/new')

  const [formData, setFormData] = useState(emptyForm())
  const [subscriptions, setSubscriptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(isEdit || isView)

  useEffect(() => {
    loadSubscriptions()
  }, [])

  useEffect(() => {
    if ((isEdit || isView) && id) {
      loadPayment(id)
    }
  }, [id, isEdit, isView])

  const loadSubscriptions = async () => {
    try {
      const response = await subscriptionService.getAll({ limit: 500 })
      if (response.success) {
        setSubscriptions(extractSubscriptions(response))
      }
    } catch (error) {
      console.error('Error loading subscriptions for dropdown:', error)
    }
  }

  const loadPayment = async (paymentId) => {
    try {
      setInitialLoading(true)
      const response = await subscriptionPaymentService.getById(paymentId)
      if (response.success && response.data) {
        const p = response.data
        setFormData({
          paymentNumber: p.paymentNumber || '',
          subscription: p.subscription?._id || p.subscription || '',
          amount: p.amount ?? 0,
          currency: p.currency || 'INR',
          exchangeRate: p.exchangeRate ?? 1,
          inrAmount: p.inrAmount ?? 0,
          taxAmount: p.taxAmount ?? 0,
          rcmAmount: p.rcmAmount ?? 0,
          totalPaid: p.totalPaid ?? 0,
          paymentDate: p.paymentDate ? new Date(p.paymentDate).toISOString().split('T')[0] : '',
          dueDate: p.dueDate ? new Date(p.dueDate).toISOString().split('T')[0] : '',
          invoiceDate: p.invoiceDate ? new Date(p.invoiceDate).toISOString().split('T')[0] : '',
          vendorInvoiceNumber: p.vendorInvoiceNumber || '',
          billingPeriodStart: p.billingPeriodStart
            ? new Date(p.billingPeriodStart).toISOString().split('T')[0]
            : '',
          billingPeriodEnd: p.billingPeriodEnd
            ? new Date(p.billingPeriodEnd).toISOString().split('T')[0]
            : '',
          status: p.status || 'Expected',
          paymentMethod: p.paymentMethod || 'Invoice',
          reference: p.reference || '',
          invoiceAttachmentUrl: p.invoiceAttachmentUrl || '',
          paymentProofUrl: p.paymentProofUrl || '',
          linkedExpense: p.linkedExpense?._id || p.linkedExpense || '',
          linkedVendorInvoice: p.linkedVendorInvoice?._id || p.linkedVendorInvoice || '',
          linkedAccountingEntry: p.linkedAccountingEntry || '',
          notes: p.notes || '',
        })
      } else {
        toast.error('Payment not found')
        navigate('/subscriptions/payments')
      }
    } catch (error) {
      console.error('Error loading payment:', error)
      if (!error._errorHandled) toast.error('Failed to load payment')
      navigate('/subscriptions/payments')
    } finally {
      setInitialLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type } = e.target
    if (name === 'subscription') {
      const selected = subscriptions.find((s) => s._id === value)
      setFormData((prev) => ({
        ...prev,
        subscription: value,
        currency: selected?.currency || prev.currency,
        amount: prev.amount || selected?.monthlyCost || 0
      }))
      return
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }))
  }

  const handleReset = () => {
    if (isEdit || isView) {
      loadPayment(id)
    } else {
      setFormData(emptyForm())
    }
  }

  const handleSave = async () => {
    if (!formData.subscription) {
      toast.error('Subscription is required')
      return
    }
    if (formData.amount == null || Number(formData.amount) < 0) {
      toast.error('Valid amount is required')
      return
    }

    const payload = {
      subscription: formData.subscription,
      amount: Number(formData.amount),
      currency: formData.currency,
      exchangeRate: Number(formData.exchangeRate) || 1,
      inrAmount: Number(formData.inrAmount) || 0,
      taxAmount: Number(formData.taxAmount) || 0,
      rcmAmount: Number(formData.rcmAmount) || 0,
      totalPaid: Number(formData.totalPaid) || 0,
      paymentDate: formData.paymentDate || undefined,
      dueDate: formData.dueDate || undefined,
      invoiceDate: formData.invoiceDate || undefined,
      vendorInvoiceNumber: formData.vendorInvoiceNumber || undefined,
      billingPeriodStart: formData.billingPeriodStart || undefined,
      billingPeriodEnd: formData.billingPeriodEnd || undefined,
      status: formData.status,
      paymentMethod: formData.paymentMethod,
      reference: formData.reference,
      invoiceAttachmentUrl: formData.invoiceAttachmentUrl || undefined,
      paymentProofUrl: formData.paymentProofUrl || undefined,
      linkedExpense: formData.linkedExpense || undefined,
      linkedVendorInvoice: formData.linkedVendorInvoice || undefined,
      linkedAccountingEntry: formData.linkedAccountingEntry || undefined,
      notes: formData.notes,
    }

    try {
      setLoading(true)
      let response
      if (isEdit && id) {
        response = await subscriptionPaymentService.update(id, payload)
      } else {
        response = await subscriptionPaymentService.create(payload)
      }
      if (response.success) {
        toast.success(isEdit ? 'Payment updated' : 'Payment recorded')
        navigate('/subscriptions/payments')
      } else {
        toast.error(response.message || 'Failed to save payment')
      }
    } catch (error) {
      console.error('Error saving payment:', error)
      if (!error._errorHandled) toast.error('Failed to save payment')
    } finally {
      setLoading(false)
    }
  }

  const pageTitle = isView ? 'View Payment' : isEdit ? 'Edit Payment' : 'New Payment'
  const disabled = isView || loading || initialLoading

  const breadcrumbItems = [
    { label: 'Subscriptions', path: '/subscriptions', onClick: () => navigate('/subscriptions') },
    { label: 'Transactions', path: '/subscriptions', onClick: () => navigate('/subscriptions') },
    { label: 'Payments', path: '/subscriptions/payments', onClick: () => navigate('/subscriptions/payments') },
    { label: pageTitle, isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{pageTitle}</h1>
          <p>{isView ? 'Payment details (read-only)' : 'Link a payment to a subscription'}</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/subscriptions/payments')}>
            <ArrowLeft size={16} />
            Back to List
          </button>
          {!isView && (
            <>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleReset} disabled={loading}>
                <RotateCcw size={16} />
                Reset
              </button>
              <button type="button" className="btn-primary" onClick={handleSave} disabled={loading || initialLoading}>
                <Save size={16} />
                {loading ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      {initialLoading ? (
        <p className="text-muted">Loading payment…</p>
      ) : (
        <div className="form-container">
          <div className="sac-form-header">
            <h2>Payment Entry</h2>
          </div>
          <div className="form-content">
            <div className="section-header">
              <h3>Payment Details</h3>
            </div>
            <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Payment #</label>
              <input
                type="text"
                name="paymentNumber"
                className="form-control"
                value={formData.paymentNumber || (isCreate ? 'Auto-generated on save' : '')}
                disabled
              />
            </div>
            <div className="col-md-8">
              <label className="form-label">Subscription *</label>
              <select
                name="subscription"
                className="form-control"
                value={formData.subscription}
                onChange={handleChange}
                disabled={disabled}
              >
                <option value="">Select subscription</option>
                {subscriptions.map((s) => (
                  <option key={s._id} value={s._id}>
                    {[s.subscriptionNumber, s.subscriptionName].filter(Boolean).join(' — ')}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Payment Date</label>
              <input
                type="date"
                name="paymentDate"
                className="form-control"
                value={formData.paymentDate}
                onChange={handleChange}
                disabled={disabled}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Amount *</label>
              <input
                type="number"
                name="amount"
                className="form-control"
                min="0"
                step="0.01"
                value={formData.amount}
                onChange={handleChange}
                disabled={disabled}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Currency</label>
              <input
                type="text"
                name="currency"
                className="form-control"
                value={formData.currency}
                onChange={handleChange}
                disabled={disabled}
                maxLength={3}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Exchange rate</label>
              <input type="number" name="exchangeRate" className="form-control" step="0.0001" value={formData.exchangeRate} onChange={handleChange} disabled={disabled} />
            </div>
            <div className="col-md-4">
              <label className="form-label">INR amount</label>
              <input type="number" name="inrAmount" className="form-control" value={formData.inrAmount} onChange={handleChange} disabled={disabled} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Tax amount</label>
              <input type="number" name="taxAmount" className="form-control" value={formData.taxAmount} onChange={handleChange} disabled={disabled} />
            </div>
            <div className="col-md-4">
              <label className="form-label">RCM amount</label>
              <input type="number" name="rcmAmount" className="form-control" value={formData.rcmAmount} onChange={handleChange} disabled={disabled} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Vendor invoice #</label>
              <input type="text" name="vendorInvoiceNumber" className="form-control" value={formData.vendorInvoiceNumber} onChange={handleChange} disabled={disabled} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Invoice date</label>
              <input type="date" name="invoiceDate" className="form-control" value={formData.invoiceDate} onChange={handleChange} disabled={disabled} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Due date</label>
              <input type="date" name="dueDate" className="form-control" value={formData.dueDate} onChange={handleChange} disabled={disabled} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Linked expense ID</label>
              <input type="text" name="linkedExpense" className="form-control" value={formData.linkedExpense} onChange={handleChange} disabled={disabled} placeholder="ExpenseClaim ObjectId" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Linked vendor invoice ID</label>
              <input type="text" name="linkedVendorInvoice" className="form-control" value={formData.linkedVendorInvoice} onChange={handleChange} disabled={disabled} placeholder="VendorInvoice ObjectId" />
            </div>
            </div>

            <div className="form-section-divider">
              <div className="section-header">
                <h3>Billing Period &amp; Status</h3>
              </div>
            </div>
            <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Billing Period Start</label>
              <input
                type="date"
                name="billingPeriodStart"
                className="form-control"
                value={formData.billingPeriodStart}
                onChange={handleChange}
                disabled={disabled}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Billing Period End</label>
              <input
                type="date"
                name="billingPeriodEnd"
                className="form-control"
                value={formData.billingPeriodEnd}
                onChange={handleChange}
                disabled={disabled}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Status</label>
              <select name="status" className="form-control" value={formData.status} onChange={handleChange} disabled={disabled}>
                {PAYMENT_STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Payment Method</label>
              <select name="paymentMethod" className="form-control" value={formData.paymentMethod} onChange={handleChange} disabled={disabled}>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="col-md-8">
              <label className="form-label">Reference</label>
              <input
                type="text"
                name="reference"
                className="form-control"
                value={formData.reference}
                onChange={handleChange}
                disabled={disabled}
              />
            </div>
            </div>

            <div className="form-section-divider">
              <div className="section-header">
                <h3>Notes</h3>
              </div>
            </div>
            <div className="row g-3">
            <div className="col-12">
              <label className="form-label">Notes</label>
              <textarea
                name="notes"
                className="form-control"
                rows={3}
                value={formData.notes}
                onChange={handleChange}
                disabled={disabled}
              />
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PaymentForm
